import {
  MedigapPlansField,
  useMedigapPlansLazyQuery,
  Sort,
  MedigapPlansQuery
} from '@coverright/data-access/types/medigap';
import { mainBackendApolloClient } from '@coverright/data-access/apollo-clients';
import { isEffectiveDateValid, useDebouncedEffect } from '@coverright/utils';
import { PlanSelector, PlanSelectorProps } from './plan-selector';
import { useMemo, useState } from 'react';
import { Gender, PlanYear } from '@coverright/data-access/types/enrollment';
import { Box } from '@mui/material';
import { Tooltip } from '@coverright/ui/inputs';

type Plan = {bidId: string, planName?: string | null, planYear?: PlanYear | null};
type MgPlan = MedigapPlansQuery['medigapPlans']['data'][0]

interface MgProps extends Omit<PlanSelectorProps ,'onChange' | 'onLoaded'> {
  onChange: (plan: MgPlan) => void;
  onLoaded?: (plans: MgPlan[]) => void;
  age: number;
  tobacco: boolean;
  gender: Gender;
  effectiveDate?: string;
  planName?: string | null;
}

export const MgPlanSelector = (props: MgProps) => {
  const {zip, county, planYear, carrierName, onChange, disabled, onLoaded, gender, age, effectiveDate, tobacco, ...rest} = props;

  const [plans, setPlans] = useState<MgPlan[]>([]);

  const [getPlans, {loading}] = useMedigapPlansLazyQuery({
    fetchPolicy: 'no-cache',
    client: mainBackendApolloClient,
    onCompleted: data => {
      setPlans(data.medigapPlans.data);
      onLoaded && onLoaded(data.medigapPlans.data);
    }
  })

  useDebouncedEffect(() => {
    if (county && zip && isEffectiveDateValid(effectiveDate)) {
      getPlans({
        variables: {
          page: {
            size: 1000,
            page: 0
          },
          sort: [{direction: Sort.Asc, field: MedigapPlansField.PlanName}],
          filter: {
            zip,
            county,
            age,
            effectiveDate,
            tobacco,
            gender,
            monthlyPlanPremiumRanges: [],
            planNames: [],
            companies: carrierName ? [carrierName] : [],
          }
        }
      })
    }
  }, 300, [carrierName, county, zip, planYear, tobacco, gender, age, effectiveDate]);

  const handleChange = (val: Plan) => {
    const plan = plans.find(p => p.key === val.bidId);
    if (plan) {
      onChange(plan);
    } else {
      throw Error('Can\'t select plan ' + JSON.stringify(val));
    }
  }



  const options = useMemo(() => {
    const result = plans.map(p => ({
      planName: `${p.title} (${p.planName})`,
      bidId: p.key
    }));

    const valueInList = plans.some(p => p.key === props.value);

    if (props.value && !valueInList) {
      result.push({
        bidId: props.value,
        planName: `${carrierName} (${props.planName})`
      })
    }

    return result;
  }, [plans, props.value]);

  return <Tooltip sx={{flex: 1, display: 'flex'}} title={carrierName ? '' : 'You have to select carrier to select plan'}>
    <Box>
      <PlanSelector {...rest}
                    zip={zip}
                    county={county}
                    onChange={handleChange}
                    disabled={disabled || loading}
                    hideBids
                    plans={options}/>
    </Box>
  </Tooltip>;

};
